import DateDisplay from '@app/src/components/DateDisplay'
import { useProviderRiskLabel } from '@app/src/hooks/useProviderRiskLabel'
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import AnswerFlagCard from '@app/src/pages/TopicalRisk/CompanyPage/AnswerFlagCard'
import TopicalRiskAreaCard from '@app/src/pages/TopicalRisk/CompanyPage/TopicalRiskAreaCard'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicProviderRisk } from '@app/src/types/topicalRisk'
import {
  ensureAllSustainabilityAreas,
  getRiskTextSecondaryColor,
  getRiskWeakerColor,
} from '@app/src/utils/riskStatusHelpers'
import { Box, Divider, Grid, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CompanyPageSection } from './CompanyPageSection'

type AutomatedRiskSectionProps = {
  providerRisk: SustainabilityTopicProviderRisk | null
  isLoading: boolean
}

const areaGridItemProps = { xs: 12, sm: 6, lg: 4, xl: 3 } as const

export const AutomatedRiskSection = ({ providerRisk, isLoading }: AutomatedRiskSectionProps) => {
  const providerRiskLabel = useProviderRiskLabel()

  const areaRisks = useMemo(() => {
    if (!providerRisk) return []

    return ensureAllSustainabilityAreas(providerRisk.sustainabilityAreaRisks)
  }, [providerRisk])

  const filteredAnswerFlags = useMemo(() => {
    if (!providerRisk?.answerFlagRuleResults) return []

    return providerRisk.answerFlagRuleResults.filter(result => !result.supersededAt)
  }, [providerRisk])

  return (
    <CompanyPageSection title={providerRiskLabel}>
      {isLoading || !providerRisk ? (
        <LoadingSkeleton areaGridItemProps={areaGridItemProps} />
      ) : (
        <>
          <HighestRiskBanner providerRisk={providerRisk} />

          <Box pt={2}>
            <Grid container spacing={2}>
              {areaRisks.map(areaRisk => (
                <Grid item {...areaGridItemProps} key={areaRisk.sustainabilityArea}>
                  <TopicalRiskAreaCard
                    sustainabilityArea={areaRisk.sustainabilityArea}
                    topicRisks={areaRisk.sustainabilityTopicRisks}
                  />
                </Grid>
              ))}
              <Grid item {...areaGridItemProps} key="answerFlag">
                <AnswerFlagCard answerFlagRuleResults={filteredAnswerFlags} />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </CompanyPageSection>
  )
}

const LoadingSkeleton = ({
  areaGridItemProps,
}: {
  areaGridItemProps: { xs: number; sm: number; lg: number; xl: number }
}) => (
  <>
    <Skeleton variant="rounded" height={50} animation="wave" sx={{ mb: 2 }} />
    <Box pt={2}>
      <Grid container spacing={2}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid item {...areaGridItemProps} key={index}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '6px', height: '100%', borderColor: 'grey.200' }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Skeleton variant="circular" width={32} height={32} animation="wave" />
                <Skeleton variant="text" width="40%" height={24} animation="wave" />
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: '6px',
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Skeleton variant="text" width="60%" height={20} animation="wave" />
                    <Skeleton variant="rounded" width={80} height={24} animation="wave" />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  </>
)

const HighestRiskBanner = ({ providerRisk }: { providerRisk: SustainabilityTopicProviderRisk }) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const createdAt = providerRisk.createdAt
  const riskStatus = providerRisk.riskStatus
  const bannerBgColor = getRiskWeakerColor(theme, riskStatus)
  const secondaryTextColor = getRiskTextSecondaryColor(theme, riskStatus)
  const bannerMessage =
    riskStatus === RiskStatus.NotApplicable
      ? formatMessage({ id: 'topicalRisk.noDataBanner' })
      : formatMessage({ id: 'topicalRisk.highestRisk' })

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: bannerBgColor,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        px: 2,
        py: 1,
        boxShadow: '0 0 0 1px inset rgba(0, 0, 0, 0.015)',
        borderRadius: '6px',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <SimpleRiskChip riskStatus={riskStatus} larger />
        <Box pr={5}>
          <Typography variant="caption" color={secondaryTextColor} noWrap>
            {formatMessage({ id: 'general.lastUpdated' })}
          </Typography>
          <Box mt="-3px">
            <DateDisplay value={createdAt} />
          </Box>
        </Box>
      </Stack>
      <Box
        color={secondaryTextColor}
        lineHeight={1.6}
        sx={{
          width: { xs: '100%', md: '50%' },
          ml: { xs: 'inherit', md: 'auto' },
          mb: { xs: 0.5, md: 0.5 },
          mt: { xs: 1, md: 0.5 },
          textAlign: { xs: 'left', md: 'right' },
        }}
      >
        {bannerMessage}
      </Box>
    </Paper>
  )
}
